<template>
  <div ref="containerRef" class="ui-subscription" :class="containerClasses">
    <div class="ui-subscription__info">
      <h3 class="ui-subscription__plan">
        Your current plan is
        <span class="ui-subscription__name">{{ planName }}</span>
      </h3>
      <div v-if="isTrial" class="ui-subscription__trial">
        <div class="ui-subscription__trial-message">
          You are on a free trial period
        </div>
      </div>
      <div class="ui-subscription__description">
        {{ planDescription }}
        <span class="ui-subscription__status" :class="statusClasses">{{ statusDescription }}</span>
      </div>
      <div class="ui-subscription__included">
        <h4 class="ui-subscription__included-caption">What’s included</h4>
        <div class="ui-subscription__included-line" />
      </div>
      <SubscriptionPlanFeatures
        :features
        class="ui-subscription__features"
      />
    </div>
    <div class="ui-subscription__price">
      <template v-if="showPrice">
        <p class="ui-subscription__price-caption">Price</p>
        <p class="ui-subscription__price-value">
          {{ priceValue }}<small v-if="periodUnit">/{{ periodUnit }}</small>
        </p>
      </template>
      <div class="ui-subscription__buttons">
        <UIButton
          v-if="isActive"
          :loading
          :disabled
          :leading="DocumentCurrencyDollarIcon"
          label="Edit Billing"
          full
          @click="handleClickBilling"
        />
        <UIButton
          v-else
          :loading
          :disabled
          :leading="DocumentCurrencyDollarIcon"
          label="Subscribe"
          full
          @click="handleClickSubscribe"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ComputedRef, inject, ref, useTemplateRef } from 'vue'
import { DateTime } from 'luxon'

import { UserPlan, UserSubscription, UserSubscriptionStatus } from '@types'

import { stringToLocalDateString } from '@/helpers/dates'
import { numberFormat } from '@/helpers/numbers'

import SubscriptionPlanFeatures from './SubscriptionPlanFeatures.vue'
import { UIButton } from '@ui/buttons'
import { DocumentCurrencyDollarIcon } from '@heroicons/vue/24/solid'
import { useResizeObserver } from '@vueuse/core'

const MIN_WIDTH = 620

type Props = {
  subscription: UserSubscription
  plan: Partial<UserPlan>
  features: string[]

  showPrice?: boolean
}

type Emits = {
  'click:subscribe': [callback: () => void]
  'click:billing': [callback: () => void]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const currency = inject<ComputedRef<string>>('currency')

const containerRef = useTemplateRef('containerRef')

const loading = ref(false)

const isShortMode = ref(false)

useResizeObserver(containerRef, entries => {
  isShortMode.value = entries[0]?.target.parentElement
    ? entries[0]?.target.parentElement.clientWidth < MIN_WIDTH
    : false
})

const endDate = computed(() => stringToLocalDateString(props.subscription.next_billing_at || props.subscription.end_date))

const disabled = computed(() => loading.value)

const isEnded = computed(() => {
  const date1 = DateTime.fromISO(props.subscription.end_date)
  const date2 = DateTime.now()
  const diff = date1.diff(date2, ['seconds'])
  return diff.seconds < 0
})

const planName = computed(() => props.plan.name)
const planDescription = computed(() => props.plan.description)
const periodUnit = computed(() => props.plan.period_unit)

const isTrial = computed(() => props.subscription.status === UserSubscriptionStatus.IN_TRIAL)

const isActive = computed(() => props.subscription.status === UserSubscriptionStatus.ACTIVE)

const statusDescription = computed(() => {
  if (isEnded.value) {
    return `Subscription ended on ${endDate.value}`
  }
  if (isTrial.value) {
    return `Trial ends on ${endDate.value}`
  }
  return `Next Payment on ${endDate.value}`
})

const containerClasses = computed(() => ({
  'ui-subscription--short': isShortMode.value,
}))

const statusClasses = computed(() => {
  if (isEnded.value) {
    return 'ui-subscription__status--ended'
  }
})

const priceValue = computed(() =>
  numberFormat(props.plan?.price || 0, {
    currency: currency?.value,
    fractionDigits: 2,
  }),
)

const handleClickSubscribe = () => {
  loading.value = true
  emit('click:subscribe', () => {
    loading.value = false
  })
}

const handleClickBilling = () => {
  loading.value = true
  emit('click:billing', () => {
    loading.value = false
  })
}
</script>

<style>
.ui-subscription {
  @apply w-full max-w-3xl;
  @apply flex flex-row;
  @apply gap-4 p-4;
  @apply rounded-3xl;
  @apply ring-1 ring-inset ring-gray-200 dark:ring-gray-600;

  &--short {
    @apply flex-col;
  }

  &__info {
    @apply flex flex-col;
    @apply gap-2 py-2 px-6;
  }

  &--short &__info {
    @apply px-2;
  }

  &__plan {
    @apply text-2xl font-bold tracking-tight;
    @apply text-gray-900 dark:text-gray-100;
  }

  &__name {
    @apply bg-indigo-500;
    @apply py-0.5 px-2 ml-1;
    @apply rounded-full;
    @apply text-white uppercase;
    @apply text-base font-normal tracking-normal;
  }

  &__trial,
  &__description {
    @apply text-base leading-normal;
    @apply text-gray-600 dark:text-gray-400;
  }

  &__status {
    @apply text-base sm:text-sm;

    &--ended {
      @apply text-red-500;
    }
  }

  &__trial-message {
    @apply text-green-700 dark:text-green-400;
  }
  &__included {
    @apply flex items-center;
    @apply gap-x-4;
  }

  &__included-caption {
    @apply flex-none;
    @apply text-sm font-semibold leading-6;
    @apply text-indigo-600 dark:text-indigo-400;
  }

  &__included-line {
    @apply h-px;
    @apply flex-auto;
    @apply bg-gray-100 dark:bg-gray-600;
  }

  &__features {
    @apply max-w-96;
  }

  &__price {
    @apply w-1/2 shrink-0;
    @apply flex flex-col items-center justify-center;
    @apply gap-4 py-16 px-6;
    @apply bg-gray-50 dark:bg-gray-850;
    @apply rounded-2xl;
    @apply ring-1 ring-inset ring-gray-900/5 dark:ring-gray-800;
  }

  &--short &__price {
    @apply w-full;
    @apply py-10;
  }

  &__price-caption {
    @apply text-base font-semibold;
    @apply text-gray-600 dark:text-gray-400;
  }

  &__price-value {
    @apply text-5xl font-bold tracking-tight;
    @apply text-gray-600 dark:text-gray-400;

    small {
      @apply font-normal tracking-normal;
      @apply text-lg;
    }
  }

  &__buttons {
    @apply w-full;
    @apply flex flex-col;
    @apply gap-2 mt-2;
  }
}
</style>
